import React, { useEffect, useState } from 'react';
import { Api } from 'services';
import NoDataIcon from '../../../Assets/ill-no-data.svg';
import { COLORS } from 'components/UserDashboardV2/utils/constants';
import { fluid, fluidVh } from 'components/UserDashboardV2/utils/responsive';
import { CourseBadge, ProgressBar } from './ProfileCommonComponents';

const isLiveCourseRow = (course) => course.courseType === "course" || course.courseType === "live" || course.badge?.label === "Classroom course";

// Total number of batches in a /standards response.
const countStandards = (res) => {
    if (Array.isArray(res)) return res.length;
    if (Array.isArray(res?.data)) return res.data.length;
    if (res?.data && typeof res.data === "object") return Object.values(res.data).flat().length;
    return 0;
};

const BatchRow = ({ batch, canChange, onChangeBatch, onUnassignBatch }) => (
    <div style={{ width: "100%", backgroundColor: "#F3F3F3", borderRadius: fluid(16), padding: `${fluid(16)} ${fluid(20)}`, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(14) }}>
        <div style={{ display: "flex", alignItems: "center", gap: fluid(16) }}>
            <div style={{
                width: fluid(64), height: fluid(64), borderRadius: fluid(12), backgroundColor: batch.color,
                flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: fluid(20), letterSpacing: 0, color: "#FFFFFF" }}>
                    {batch.code}
                </span>
            </div>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: fluid(4) }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, color: "#1A1A1A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {batch.name}
                </span>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(14), letterSpacing: 0, color: "#666666" }}>
                    {batch.details}
                </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: fluid(24), flexShrink: 0 }}>
                {canChange && (
                    <button
                        type="button"
                        onClick={() => onChangeBatch && onChangeBatch(batch)}
                        style={{ border: "none", background: "transparent", padding: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: fluid(16), letterSpacing: 0, color: "#594099", cursor: "pointer" }}
                    >
                        CHANGE
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onUnassignBatch && onUnassignBatch(batch)}
                    style={{ border: "none", background: "transparent", padding: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: fluid(16), letterSpacing: 0, color: "#FF414D", cursor: "pointer" }}
                >
                    UNASSIGN
                </button>
            </div>
        </div>
        <ProgressBar percent={batch.progress} />
    </div>
);

const EnrolledCourseCard = ({ course, totalBatches, onAssignNewBatch, onUnassign, onChangeBatch, onUnassignBatch, onAssignBatch }) => {
    const hasBatches = Array.isArray(course.batches) && course.batches.length > 0;
    const isLiveCourse = isLiveCourseRow(course);
    // CHANGE / ASSIGN NEW BATCH only when the course has more than one batch AND
    // there is at least one unassigned batch remaining for the student to switch to or assign.
    // undefined = count still loading (hidden); null = count fetch failed (fallback to true).
    const assignedBatchesCount = Array.isArray(course.batches) ? course.batches.length : 0;
    const hasAvailableBatches = totalBatches === null
        ? true
        : typeof totalBatches === "number"
            ? totalBatches > 1 && totalBatches > assignedBatchesCount
            : false;
    const Icon = course.icon;
    return (
        <div style={{ width: "100%", backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(20), boxSizing: "border-box", display: "flex", flexDirection: "column", gap: fluid(20) }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: fluid(16) }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: fluid(16), minWidth: 0, flex: 1 }}>
                    <div style={{
                        width: fluid(64), height: fluid(64), borderRadius: fluid(16), backgroundColor: course.iconBg,
                        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <Icon size={32} color={course.iconColor} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: fluid(8), minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(12), flexWrap: "wrap" }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 600, fontSize: fluid(20), letterSpacing: 0, color: "#1A1A1A" }}>
                                {course.title}
                            </span>
                            <CourseBadge {...course.badge} />
                        </div>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, color: "#636363" }}>
                            Enrolled {course.enrolledOn}
                        </span>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onUnassign && onUnassign(course)}
                    style={{ border: "none", background: "transparent", padding: 0, flexShrink: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: fluid(16), letterSpacing: 0, color: "#FF414D", cursor: "pointer" }}
                >
                    UNASSIGN
                </button>
            </div>

            {hasBatches ? (
                <div style={{ display: "flex", flexDirection: "column", gap: fluid(12) }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, color: "#636363" }}>
                            Batches ({course.batches.length})
                        </span>
                        {hasAvailableBatches && (
                            <button
                                type="button"
                                onClick={() => onAssignNewBatch && onAssignNewBatch(course)}
                                style={{ border: "none", background: "transparent", padding: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: fluid(16), letterSpacing: 0, color: "#594099", cursor: "pointer" }}
                            >
                                ASSIGN NEW BATCH
                            </button>
                        )}
                    </div>
                    {course.batches.map((batch) => (
                        <BatchRow
                            key={batch.id}
                            batch={batch}
                            canChange={hasAvailableBatches}
                            onChangeBatch={(b) => onChangeBatch && onChangeBatch(course, b)}
                            onUnassignBatch={(b) => onUnassignBatch && onUnassignBatch(course, b)}
                        />
                    ))}
                </div>
            ) : isLiveCourse ? (
                <div style={{ display: "flex", flexDirection: "column", gap: fluid(12) }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, fontSize: fluid(16), letterSpacing: 0, color: "#636363" }}>
                            No batch assigned (0)
                        </span>
                        <button
                            type="button"
                            onClick={() => onAssignBatch && onAssignBatch(course)}
                            style={{ border: "none", background: "transparent", padding: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: fluid(16), letterSpacing: 0, color: "#594099", cursor: "pointer" }}
                        >
                            ASSIGN BATCH
                        </button>
                    </div>
                </div>
            ) : (
                <ProgressBar percent={course.progress} />
            )}
        </div>
    );
};

const CoursesAndBatchesTab = ({
    enrolledCourses,
    enrolledCoursesLoading,
    onEnrollClick,
    onAssignNewBatch,
    onUnassign,
    onUnassignCourse,
    onChangeBatch,
    onUnassignBatch,
    onAssignBatch,
}) => {
    const handleUnassignCourse = onUnassign || onUnassignCourse;
    // Total batches per live course (courseId -> count), fetched once per course.
    const [batchCounts, setBatchCounts] = useState({});

    useEffect(() => {
        const courseIds = [...new Set(
            (enrolledCourses || [])
                .filter(c => isLiveCourseRow(c) && Array.isArray(c.batches) && c.batches.length > 0 && c.courseId)
                .map(c => String(c.courseId))
        )].filter(id => !(id in batchCounts));
        courseIds.forEach((courseId) => {
            // timed=true so per-course requests to the same URL don't overwrite each other.
            Api.get("/standards").params({ courseId }).send((res, error) => {
                setBatchCounts(prev => ({ ...prev, [courseId]: error ? null : countStandards(res) }));
            }, true);
        });
    }, [enrolledCourses]);

    return (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: fluid(12), flexShrink: 0 }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500, fontSize: fluid(24), letterSpacing: 0, color: "#191919" }}>
                    Enrolled courses ({enrolledCourses.length})
                </span>
                <button
                    type="button"
                    onClick={onEnrollClick}
                    style={{
                        height: fluid(45),
                        padding: `${fluid(13)} ${fluid(24)}`,
                        boxSizing: "border-box",
                        borderRadius: fluid(12),
                        border: "none",
                        backgroundColor: COLORS.primary,
                        color: "#FFFFFF",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: fluid(16),
                        fontWeight: 700,
                        letterSpacing: 0,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap",
                    }}
                >
                    ENROLL IN COURSE
                </button>
            </div>

            <div
                className="staff-profile-info-scroll"
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: fluid(20),
                    marginTop: fluid(20),
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                }}
            >
                {enrolledCoursesLoading ? null : enrolledCourses.length === 0 ? (
                    <div style={{
                        flex: 1,
                        width: "100%",
                        minHeight: fluidVh(450),
                        backgroundColor: "#FFFFFF",
                        borderRadius: fluid(24),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: `${fluid(60)} ${fluid(24)}`,
                        boxSizing: "border-box",
                        gap: fluid(12),
                    }}>
                        <img src={NoDataIcon} alt="No courses found" style={{ width: fluid(220), height: fluid(220) }} />
                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(24), fontWeight: 600, color: "#191919" }}>
                            No courses found
                        </span>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#666666" }}>
                            There are no enrolled courses assigned to this student yet.
                        </span>
                    </div>
                ) : enrolledCourses.map((course) => (
                    <EnrolledCourseCard
                        key={course.id}
                        course={course}
                        totalBatches={batchCounts[String(course.courseId)]}
                        onAssignNewBatch={onAssignNewBatch}
                        onUnassign={handleUnassignCourse}
                        onChangeBatch={onChangeBatch}
                        onUnassignBatch={onUnassignBatch}
                        onAssignBatch={onAssignBatch}
                    />
                ))}
            </div>
        </div>
    );
};

export default CoursesAndBatchesTab;
